"use client";

import { createContext, useContext, useState, ReactNode } from "react";
import { useRouter } from "next/navigation";
import getAuthToken from "src/utils/getAuthToken";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "src/components/ui/alert-dialog";
import { useTranslation } from "react-i18next";

// Define the type for the Auth Context
interface AuthContextType {
  checkAuth: () => boolean;
}

// Create Auth Context with proper default value
const AuthContext = createContext<AuthContextType>({
  checkAuth: () => true,
});

export const AuthAlertProvider = ({ children }: { children: ReactNode }) => {
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
  const router = useRouter();
  const { t } = useTranslation();

  const checkAuth = (): boolean => {
    const token = getAuthToken();
    if (!token) {
      setIsAlertDialogOpen(true);
      return false;
    }
    return true;
  };

  return (
    <AuthContext.Provider value={{ checkAuth }}>
      {children}

      {/* Automatically Render AlertDialog at the Top Level */}
      <AlertDialog open={isAlertDialogOpen} onOpenChange={setIsAlertDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-primary">
              {t("login_required")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("you_need_to_be_logged_in_to_proceed_with_the_purchase")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsAlertDialogOpen(false)}>
              {t("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="border-primary bg-primary text-white"
              onClick={() => router.push("/auth/login")}
            >
              {t("login")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AuthContext.Provider>
  );
};

// Hook to use authentication
export const useAuthCheck = () => useContext(AuthContext);
